import { createStore } from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import { SET_USER } from './mutation-types'
import { UserApi } from '@/services/users/'
import { resetNotificationState } from '@/services/notifications'

import { STORAGE_KEY } from './storageKey'

export { STORAGE_KEY }

const state = {
  user: null,
}

const mutations = {
  [SET_USER]: (state, payload) => {
    state.user = payload
    // Reset notification state when user logs out (user is set to null)
    if (!payload) {
      resetNotificationState()
    }
  },

  // ✅ Optional: helper mutation to preserve token automatically
  SET_USER_WITH_PRESERVED_TOKEN(state, newUser) {
    const token = state.user?.token || null
    state.user = { ...newUser, token }
  },
}

const actions = {
  setUser({ commit }, user) {
    // The staff MFA flow used to stash the password on the user object, which
    // vuex-persistedstate then wrote to localStorage. Drop it here so a leftover
    // caller cannot persist credentials even if it still passes them.
    if (user && Object.prototype.hasOwnProperty.call(user, 'password')) {
      const { password: _password, ...safeUser } = user
      commit(SET_USER, safeUser)
      return
    }
    commit(SET_USER, user)
  },

  async fetchUser({ commit, getters }) {
    try {
      const userId = getters.user?.id
      if (!userId) return

      const res = await UserApi.csc.customRetrieve({ id: userId })

      // ✅ Preserve the current token when updating user
      const currentToken = getters.token
      commit(SET_USER, {
        ...res,
        token: currentToken,
      })

      // Alternatively, if you want to use the helper mutation:
      // commit('SET_USER_WITH_PRESERVED_TOKEN', res)
    } catch (err) {
      console.error('Failed to fetch user:', err)
    }
  },
}

const getters = {
  isLoggedIn: (state) => !!state.user?.token,

  user: (state) => state.user,

  token: (state) => (state.user ? state.user.token : null),

  userGroups: (state) => {
    return Array.isArray(state.user?.groups) ? state.user.groups.map((group) => group.name) : []
  },

  isFundraiser: (state) => {
    return Array.isArray(state.user?.groups)
      ? state.user.groups.some((group) => group.name === 'Fundraiser')
      : false
  },

  isCanvassingOrgAdmin: (state) => {
    return Array.isArray(state.user?.groups)
      ? state.user.groups.some((group) =>
          ['Canvassing Org Admin', 'Super Canvassing Org Admin'].includes(group.name),
        )
      : false
  },

  isSuperCanvassingOrgAdmin: (state) => {
    return Array.isArray(state.user?.groups)
      ? state.user.groups.some((group) => group.name === 'Super Canvassing Org Admin')
      : false
  },

  accessibleCanvassingOrgs: (state) => {
    if (Array.isArray(state.user?.canvassingOrgs) && state.user.canvassingOrgs.length) {
      return state.user.canvassingOrgs
    }
    return state.user?.canvassingOrg ? [state.user.canvassingOrg] : []
  },

  isCanvassingOrgFieldDirector: (state) => {
    return Array.isArray(state.user?.groups)
      ? state.user.groups.some((group) => group.name === 'Canvassing Org Field Director')
      : false
  },

  isNPOAdmin: (state) => {
    return Array.isArray(state.user?.groups)
      ? state.user.groups.some((group) => group.name === 'NPO Admin')
      : false
  },

  isGivarooStaff: (state) => {
    return Array.isArray(state.user?.groups)
      ? state.user.groups.some((group) => group.name === 'Givaroo Staff')
      : false
  },
}

const store = createStore({
  state,
  mutations,
  actions,
  getters,
  modules: {},
  plugins: [
    createPersistedState({
      key: STORAGE_KEY,
    }),
  ],
})

export default store
